import { getApiError } from '../lib/apiError';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  RotateCcw, Search, Plus, Minus, Trash2, ShoppingCart, Package,
  ArrowLeftRight, DollarSign, User, FileText, Loader2, Check, Eye,
  ArrowDown, ArrowUp, AlertCircle, Receipt
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Returns = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [returns, setReturns] = useState([]);
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  
  // Dialog states
  const [newReturnDialog, setNewReturnDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Search states
  const [saleSearch, setSaleSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [exchangeProductSearch, setExchangeProductSearch] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    original_sale: null,
    seller_id: '',
    seller_name: '',
    reason: '',
    returned_items: [],
    exchange_items: [],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [returnsRes, salesRes, productsRes, sellersRes] = await Promise.all([
        axios.get(`${API_URL}/api/returns`),
        axios.get(`${API_URL}/api/sales`),
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/sellers`)
      ]);
      setReturns(returnsRes.data);
      const salesData = Array.isArray(salesRes.data) ? salesRes.data : salesRes.data.sales || [];
      setSales(salesData.filter(s => s.payment_status === 'pago'));
      setProducts(productsRes.data);
      setSellers(sellersRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleSelectSale = (sale) => {
    setFormData({
      ...formData,
      original_sale: sale,
      returned_items: [],
      exchange_items: []
    });
    setSaleSearch('');
  };

  const handleAddReturnedItem = (product, maxQty) => {
    const existing = formData.returned_items.find(i => i.product_id === product.product_id);
    if (existing) {
      if (existing.quantity < maxQty) {
        setFormData({
          ...formData,
          returned_items: formData.returned_items.map(i => 
            i.product_id === product.product_id 
              ? { ...i, quantity: i.quantity + 1, total: (i.quantity + 1) * i.unit_price }
              : i
          )
        });
      }
    } else {
      setFormData({
        ...formData,
        returned_items: [...formData.returned_items, {
          product_id: product.product_id,
          product_name: product.product_name,
          quantity: 1,
          unit_price: product.unit_price,
          total: product.unit_price,
          max_quantity: maxQty
        }]
      });
    }
    setProductSearch('');
  };

  const handleRemoveReturnedItem = (productId) => {
    setFormData({
      ...formData,
      returned_items: formData.returned_items.filter(i => i.product_id !== productId)
    });
  };

  const handleUpdateReturnedQty = (productId, delta) => {
    setFormData({
      ...formData,
      returned_items: formData.returned_items.map(i => {
        if (i.product_id === productId) {
          const newQty = Math.max(1, Math.min(i.max_quantity, i.quantity + delta));
          return { ...i, quantity: newQty, total: newQty * i.unit_price };
        }
        return i;
      })
    });
  };

  const handleAddExchangeItem = (product) => {
    const existing = formData.exchange_items.find(i => i.product_id === product.id);
    if (existing) {
      setFormData({
        ...formData,
        exchange_items: formData.exchange_items.map(i =>
          i.product_id === product.id
            ? { ...i, quantity: i.quantity + 1, total: (i.quantity + 1) * i.unit_price }
            : i
        )
      });
    } else {
      setFormData({
        ...formData,
        exchange_items: [...formData.exchange_items, {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: product.price,
          total: product.price,
          stock: product.stock
        }]
      });
    }
    setExchangeProductSearch('');
  };

  const handleRemoveExchangeItem = (productId) => {
    setFormData({
      ...formData,
      exchange_items: formData.exchange_items.filter(i => i.product_id !== productId)
    });
  };

  const handleUpdateExchangeQty = (productId, delta) => {
    setFormData({
      ...formData,
      exchange_items: formData.exchange_items.map(i => {
        if (i.product_id === productId) {
          const newQty = Math.max(1, Math.min(i.stock, i.quantity + delta));
          return { ...i, quantity: newQty, total: newQty * i.unit_price };
        }
        return i;
      })
    });
  };

  const calculateTotals = () => {
    const returnedTotal = formData.returned_items.reduce((sum, i) => sum + i.total, 0);
    const exchangeTotal = formData.exchange_items.reduce((sum, i) => sum + i.total, 0);
    const difference = exchangeTotal - returnedTotal;
    return { returnedTotal, exchangeTotal, difference };
  };

  const handleSubmit = async () => {
    if (!formData.original_sale) {
      toast.error('Selecione a venda original');
      return;
    }
    if (formData.returned_items.length === 0) {
      toast.error('Adicione ao menos um item para devolução');
      return;
    }
    if (!formData.seller_id) {
      toast.error('Selecione o vendedor responsável');
      return;
    }
    if (!formData.reason.trim()) {
      toast.error('Informe o motivo da devolução');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        original_sale_id: formData.original_sale.id,
        original_sale_number: formData.original_sale.sale_number,
        client_id: formData.original_sale.client_id,
        client_name: formData.original_sale.client_name,
        seller_id: formData.seller_id,
        seller_name: formData.seller_name,
        reason: formData.reason,
        returned_items: formData.returned_items.map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          quantity: i.quantity,
          unit_price: i.unit_price,
          total: i.total
        })),
        exchange_items: formData.exchange_items.map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          quantity: i.quantity,
          unit_price: i.unit_price,
          total: i.total
        })),
        notes: formData.notes
      };

      const response = await axios.post(`${API_URL}/api/returns`, payload);
      toast.success(response.data.message);
      
      if (response.data.generated_order) {
        toast.info(`Pedido ${response.data.generated_order.number} gerado para pagamento`);
      }
      
      setNewReturnDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao registrar devolução'));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      original_sale: null,
      seller_id: '',
      seller_name: '',
      reason: '',
      returned_items: [],
      exchange_items: [],
      notes: ''
    });
    setSaleSearch('');
    setProductSearch('');
    setExchangeProductSearch('');
  };

  const handleViewReturn = (ret) => {
    setSelectedReturn(ret);
    setViewDialog(true);
  };

  const filteredSales = sales.filter(s => 
    s.sale_number?.toLowerCase().includes(saleSearch.toLowerCase()) ||
    s.client_name?.toLowerCase().includes(saleSearch.toLowerCase())
  ).slice(0, 10);

  const saleItems = formData.original_sale?.items || [];
  const filteredSaleItems = saleItems.filter(item =>
    item.product_name?.toLowerCase().includes(productSearch.toLowerCase())
  );

  const filteredProducts = products.filter(p =>
    (p.name?.toLowerCase().includes(exchangeProductSearch.toLowerCase()) ||
    p.ean?.toLowerCase().includes(exchangeProductSearch.toLowerCase()) ||
    p.sku?.toLowerCase().includes(exchangeProductSearch.toLowerCase()) ||
    p.code?.toLowerCase().includes(exchangeProductSearch.toLowerCase())) &&
    p.stock > 0
  ).slice(0, 10);

  // Handler de Enter no campo de busca de troca (leitor de código de barras)
  const handleExchangeSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const term = exchangeProductSearch.trim();
      if (!term) return;

      // Busca exata por EAN/SKU/código
      const exactMatch = products.find(p =>
        (p.ean === term || p.sku === term || p.code === term) && p.stock > 0
      );

      if (exactMatch) {
        handleAddExchangeItem(exactMatch);
        toast.success(`${exactMatch.name} adicionado à troca`);
        return;
      }

      // Se tem apenas um resultado filtrado, usa ele
      if (filteredProducts.length === 1) {
        handleAddExchangeItem(filteredProducts[0]);
        toast.success(`${filteredProducts[0].name} adicionado à troca`);
        return;
      }

      if (filteredProducts.length > 1) {
        toast.info(`${filteredProducts.length} produtos encontrados. Selecione um da lista.`);
      } else {
        toast.error('Nenhum produto encontrado');
      }
    }
  };

  const { returnedTotal, exchangeTotal, difference } = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="returns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <RotateCcw className="text-orange-600" />
            Devoluções de Mercadorias
          </h1>
          <p className="text-slate-500 mt-1">Registre devoluções e trocas de produtos</p>
        </div>
        <Button 
          className="bg-orange-600 hover:bg-orange-700"
          onClick={() => { resetForm(); setNewReturnDialog(true); }}
          data-testid="new-return-btn"
        >
          <Plus size={18} className="mr-2" />
          Nova Devolução
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Devoluções</p>
                <p className="text-2xl font-bold text-orange-600">{returns.length}</p>
              </div>
              <RotateCcw className="text-orange-500" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Valor Devolvido</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(returns.reduce((sum, r) => sum + (r.returned_total || 0), 0))}
                </p>
              </div>
              <ArrowDown className="text-red-500" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Valor em Trocas</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(returns.reduce((sum, r) => sum + (r.exchange_total || 0), 0))}
                </p>
              </div>
              <ArrowLeftRight className="text-blue-500" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pedidos Gerados</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {returns.filter(r => r.generated_order_id).length}
                </p>
              </div>
              <Receipt className="text-emerald-500" size={32} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Returns List */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Devoluções</CardTitle>
        </CardHeader>
        <CardContent>
          {returns.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <RotateCcw size={48} className="mx-auto mb-4 text-slate-300" />
              <p>Nenhuma devolução registrada</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº Devolução</TableHead>
                  <TableHead>Venda Original</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="text-right">Devolvido</TableHead>
                  <TableHead className="text-right">Troca</TableHead>
                  <TableHead className="text-right">Diferença</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returns.map((ret) => (
                  <TableRow key={ret.id}>
                    <TableCell className="font-mono font-medium">{ret.return_number}</TableCell>
                    <TableCell className="font-mono">{ret.original_sale_number}</TableCell>
                    <TableCell>{ret.client_name}</TableCell>
                    <TableCell className="max-w-[150px] truncate">{ret.reason}</TableCell>
                    <TableCell className="text-right text-red-600">
                      {formatCurrency(ret.returned_total)}
                    </TableCell>
                    <TableCell className="text-right text-blue-600">
                      {formatCurrency(ret.exchange_total)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge className={ret.difference > 0 ? 'bg-amber-100 text-amber-800' : ret.difference < 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'}>
                        {ret.difference > 0 ? '+' : ''}{formatCurrency(ret.difference)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(ret.created_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => handleViewReturn(ret)}>
                        <Eye size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Return Dialog */}
      <Dialog open={newReturnDialog} onOpenChange={setNewReturnDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="text-orange-600" />
              Nova Devolução de Mercadoria
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Step 1: Select Original Sale */}
            <div className="space-y-3">
              <Label className="font-semibold">1. Venda Original</Label>
              {formData.original_sale ? (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono font-bold">{formData.original_sale.sale_number}</p>
                        <p className="text-sm text-slate-600">{formData.original_sale.client_name}</p>
                        <p className="text-sm text-slate-500">
                          {formatDate(formData.original_sale.created_at)} • {formatCurrency(formData.original_sale.total)}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setFormData({ ...formData, original_sale: null, returned_items: [] })}>
                        Trocar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-3 text-slate-400" size={18} />
                  <Input
                    placeholder="Buscar por número da venda ou cliente..."
                    value={saleSearch}
                    onChange={(e) => setSaleSearch(e.target.value)}
                    className="pl-10"
                    data-testid="sale-search"
                  />
                  {saleSearch && filteredSales.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredSales.map((sale) => (
                        <div
                          key={sale.id}
                          className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => handleSelectSale(sale)}
                        >
                          <div className="flex justify-between">
                            <span className="font-mono font-medium">{sale.sale_number}</span>
                            <span className="text-emerald-600 font-medium">{formatCurrency(sale.total)}</span>
                          </div>
                          <p className="text-sm text-slate-500">{sale.client_name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2: Seller */}
            <div className="space-y-2">
              <Label className="font-semibold">2. Vendedor Responsável</Label>
              <Select
                value={formData.seller_id}
                onValueChange={(v) => {
                  const seller = sellers.find(s => s.id === v);
                  setFormData({ ...formData, seller_id: v, seller_name: seller?.name || '' });
                }}
              >
                <SelectTrigger data-testid="seller-select">
                  <SelectValue placeholder="Selecione o vendedor" />
                </SelectTrigger>
                <SelectContent>
                  {sellers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 3: Reason */}
            <div className="space-y-2">
              <Label className="font-semibold">3. Motivo da Devolução</Label>
              <Textarea
                placeholder="Descreva o motivo da devolução..."
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows={2}
                data-testid="reason-input"
              />
            </div>

            {/* Step 4: Items to Return */}
            {formData.original_sale && (
              <div className="space-y-3">
                <Label className="font-semibold flex items-center gap-2">
                  <ArrowDown className="text-red-500" size={18} />
                  4. Itens para Devolução (Entrada no Estoque)
                </Label>
                
                <div className="relative">
                  <Search className="absolute left-3 top-3 text-slate-400" size={18} />
                  <Input
                    placeholder="Buscar item da venda..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pl-10"
                  />
                  {productSearch && filteredSaleItems.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredSaleItems.map((item, idx) => (
                        <div
                          key={idx}
                          className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
                          onClick={() => handleAddReturnedItem(item, item.quantity)}
                        >
                          <div>
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-sm text-slate-500">Qtd comprada: {item.quantity}</p>
                          </div>
                          <span className="text-emerald-600">{formatCurrency(item.unit_price)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {formData.returned_items.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-red-50">
                          <TableHead>Produto</TableHead>
                          <TableHead className="text-center">Qtd</TableHead>
                          <TableHead className="text-right">Preço Unit.</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.returned_items.map((item) => (
                          <TableRow key={item.product_id}>
                            <TableCell>{item.product_name}</TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => handleUpdateReturnedQty(item.product_id, -1)}>
                                  <Minus size={14} />
                                </Button>
                                <span className="w-8 text-center font-medium">{item.quantity}</span>
                                <Button variant="outline" size="sm" onClick={() => handleUpdateReturnedQty(item.product_id, 1)}>
                                  <Plus size={14} />
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.total)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleRemoveReturnedItem(item.product_id)}>
                                <Trash2 size={16} />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="bg-red-50 p-3 text-right">
                      <span className="text-sm text-slate-600 mr-2">Total Devolvido:</span>
                      <span className="font-bold text-red-600">{formatCurrency(returnedTotal)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Exchange Items (Optional) */}
            {formData.returned_items.length > 0 && (
              <div className="space-y-3">
                <Label className="font-semibold flex items-center gap-2">
                  <ArrowUp className="text-blue-500" size={18} />
                  5. Itens de Troca (Opcional - Saída do Estoque)
                </Label>
                
                <div className="relative">
                  <Search className="absolute left-3 top-3 text-slate-400" size={18} />
                  <Input
                    placeholder="Buscar produto por nome, SKU ou código de barras..."
                    value={exchangeProductSearch}
                    onChange={(e) => setExchangeProductSearch(e.target.value)}
                    onKeyDown={handleExchangeSearchKeyDown}
                    className="pl-10"
                    data-testid="exchange-product-search"
                  />
                  {exchangeProductSearch && filteredProducts.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredProducts.map((product) => (
                        <div
                          key={product.id}
                          className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
                          onClick={() => handleAddExchangeItem(product)}
                          data-testid={`exchange-product-${product.id}`}
                        >
                          <div>
                            <p className="font-medium">{product.name}</p>
                            <p className="text-sm text-slate-500">
                              {product.ean && `EAN: ${product.ean} • `}
                              {product.sku && `SKU: ${product.sku} • `}
                              Estoque: {product.stock}
                            </p>
                          </div>
                          <span className="text-emerald-600 font-medium">{formatCurrency(product.price)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {formData.exchange_items.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-blue-50">
                          <TableHead>Produto</TableHead>
                          <TableHead className="text-center">Qtd</TableHead>
                          <TableHead className="text-right">Preço Unit.</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.exchange_items.map((item) => (
                          <TableRow key={item.product_id}>
                            <TableCell>{item.product_name}</TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => handleUpdateExchangeQty(item.product_id, -1)}>
                                  <Minus size={14} />
                                </Button>
                                <span className="w-8 text-center font-medium">{item.quantity}</span>
                                <Button variant="outline" size="sm" onClick={() => handleUpdateExchangeQty(item.product_id, 1)}>
                                  <Plus size={14} />
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(item.total)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleRemoveExchangeItem(item.product_id)}>
                                <Trash2 size={16} />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="bg-blue-50 p-3 text-right">
                      <span className="text-sm text-slate-600 mr-2">Total Troca:</span>
                      <span className="font-bold text-blue-600">{formatCurrency(exchangeTotal)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Summary */}
            {formData.returned_items.length > 0 && (
              <Card className={`border-2 ${difference > 0 ? 'border-amber-500 bg-amber-50' : difference < 0 ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-slate-50'}`}>
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-slate-600">Devolvido</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(returnedTotal)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Troca</p>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(exchangeTotal)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Diferença</p>
                      <p className={`text-xl font-bold ${difference > 0 ? 'text-amber-600' : difference < 0 ? 'text-emerald-600' : 'text-slate-600'}`}>
                        {difference > 0 ? '+' : ''}{formatCurrency(difference)}
                      </p>
                    </div>
                  </div>
                  {difference > 0 && (
                    <div className="mt-3 p-2 bg-amber-100 rounded-lg text-center">
                      <AlertCircle className="inline mr-2 text-amber-600" size={18} />
                      <span className="text-amber-800">Cliente deve pagar <strong>{formatCurrency(difference)}</strong>. Um pedido será gerado.</span>
                    </div>
                  )}
                  {difference < 0 && (
                    <div className="mt-3 p-2 bg-emerald-100 rounded-lg text-center">
                      <DollarSign className="inline mr-2 text-emerald-600" size={18} />
                      <span className="text-emerald-800">Cliente tem crédito de <strong>{formatCurrency(Math.abs(difference))}</strong>.</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Observações (opcional)</Label>
              <Textarea
                placeholder="Observações adicionais..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setNewReturnDialog(false)}>
                Cancelar
              </Button>
              <Button 
                className="bg-orange-600 hover:bg-orange-700"
                onClick={handleSubmit}
                disabled={saving || !formData.original_sale || formData.returned_items.length === 0}
                data-testid="submit-return-btn"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Check size={18} className="mr-2" />}
                Registrar Devolução
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Return Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="text-orange-600" />
              Devolução {selectedReturn?.return_number}
            </DialogTitle>
          </DialogHeader>
          {selectedReturn && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Venda Original</Label>
                  <p className="font-mono font-medium">{selectedReturn.original_sale_number}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Cliente</Label>
                  <p className="font-medium">{selectedReturn.client_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Vendedor</Label>
                  <p className="font-medium">{selectedReturn.seller_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Data</Label>
                  <p className="font-medium">{formatDate(selectedReturn.created_at)}</p>
                </div>
              </div>

              <div>
                <Label className="text-slate-500">Motivo</Label>
                <p className="mt-1">{selectedReturn.reason}</p>
              </div>

              <div>
                <Label className="text-slate-500 flex items-center gap-1">
                  <ArrowDown className="text-red-500" size={14} /> Itens Devolvidos
                </Label>
                <div className="mt-2 border rounded-lg overflow-hidden">
                  <Table>
                    <TableBody>
                      {selectedReturn.returned_items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell className="text-center">{item.quantity}x</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="bg-red-50 p-2 text-right text-red-600 font-bold">
                    Total: {formatCurrency(selectedReturn.returned_total)}
                  </div>
                </div>
              </div>

              {selectedReturn.exchange_items?.length > 0 && (
                <div>
                  <Label className="text-slate-500 flex items-center gap-1">
                    <ArrowUp className="text-blue-500" size={14} /> Itens de Troca
                  </Label>
                  <div className="mt-2 border rounded-lg overflow-hidden">
                    <Table>
                      <TableBody>
                        {selectedReturn.exchange_items?.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.product_name}</TableCell>
                            <TableCell className="text-center">{item.quantity}x</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="bg-blue-50 p-2 text-right text-blue-600 font-bold">
                      Total: {formatCurrency(selectedReturn.exchange_total)}
                    </div>
                  </div>
                </div>
              )}

              <Card className={`${selectedReturn.difference > 0 ? 'bg-amber-50' : selectedReturn.difference < 0 ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                <CardContent className="p-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Diferença:</span>
                    <span className={`text-lg font-bold ${selectedReturn.difference > 0 ? 'text-amber-600' : selectedReturn.difference < 0 ? 'text-emerald-600' : 'text-slate-600'}`}>
                      {selectedReturn.difference > 0 ? '+' : ''}{formatCurrency(selectedReturn.difference)}
                    </span>
                  </div>
                  {selectedReturn.generated_order_number && (
                    <p className="text-sm text-amber-700 mt-2">
                      Pedido gerado: <span className="font-mono font-bold">{selectedReturn.generated_order_number}</span>
                    </p>
                  )}
                </CardContent>
              </Card>

              {selectedReturn.notes && (
                <div>
                  <Label className="text-slate-500">Observações</Label>
                  <p className="mt-1 text-sm">{selectedReturn.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Returns;
